import React, { useEffect, useMemo, useState } from 'react';

import { Box, Button, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded';
import GppGoodRoundedIcon from '@mui/icons-material/GppGoodRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { useNavigate } from 'react-router-dom';

import { Alerta } from '../../components/alerta';
import PageHeader from '../../components/pageHeader';
import { formatDatePtBr, getDaysUntilDate } from '../../utils/dateUtils';
import { fetchFlask } from '../../utils/fetchFlask';
import '../css/nrsBI.css';

const expirationWindowDays = 30;

const nrColumns = [
    { label: 'NR-10', validityKey: 'validade_nr10' },
    { label: 'NR-35', validityKey: 'validade_nr35' },
    { label: 'NR-10SEP', validityKey: 'validade_nr10sep' }
];

const getStatus = (validade) => {
    if (!validade) {
        return null;
    }

    const daysUntilExpiration = getDaysUntilDate(validade);

    if (daysUntilExpiration < 0) {
        return {
            className: 'is-expired',
            icon: <ReportProblemRoundedIcon />,
            label: 'Vencido'
        };
    }

    if (daysUntilExpiration <= expirationWindowDays) {
        return {
            className: 'is-warning',
            icon: <WarningAmberRoundedIcon />,
            label: 'A vencer'
        };
    }

    return {
        className: 'is-ok',
        icon: <TaskAltRoundedIcon />,
        label: 'Vigente'
    };
};

const ControleNRsBI = () => {
    const navigate = useNavigate();
    const [registros, setRegistros] = useState([]);
    const [selectedSite, setSelectedSite] = useState('all');
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);

    useEffect(() => {
        async function fetchReportData () {
            const nrsResponse = await fetchFlask('listarNRs');
            if (nrsResponse.status === 'error') {
                setMessage(nrsResponse.message);
                setStatus(nrsResponse.status);
                setOpenAlerta(true);
                return;
            }

            setRegistros(nrsResponse);
        }

        fetchReportData();
    }, []);

    const sites = useMemo(() => [...new Set(registros.map((registro) => registro.site))], [registros]);

    const filteredRegistros = useMemo(() => {
        if (selectedSite === 'all') {
            return registros;
        }

        return registros.filter((registro) => registro.site === selectedSite);
    }, [registros, selectedSite]);

    const kpis = useMemo(() => {
        const certficados = filteredRegistros.flatMap((registro) =>
            nrColumns
                .map((column) => registro[column.validityKey])
                .filter(Boolean)
        );

        const vencidos = certficados.filter((validade) => getDaysUntilDate(validade) < 0).length;
        const aVencer = certficados.filter((validade) => {
            const daysUntilExpiration = getDaysUntilDate(validade);
            return daysUntilExpiration >= 0 && daysUntilExpiration <= expirationWindowDays;
        }).length;
        const vigentes = certficados.length - vencidos - aVencer;

        return {
            funcionarios: filteredRegistros.length,
            certificados: certficados.length,
            vencidos,
            aVencer,
            vigentes
        };
    }, [filteredRegistros]);

    const selectedSiteLabel = selectedSite === 'all'
        ? 'Todos os sites'
        : selectedSite;
    const visibleSiteCount = selectedSite === 'all' ? sites.length : 1;
    const okPercent = kpis.certificados > 0 ? (kpis.vigentes / kpis.certificados) * 100 : 0;
    const warningPercent = kpis.certificados > 0 ? (kpis.aVencer / kpis.certificados) * 100 : 0;
    const expiredPercent = kpis.certificados > 0 ? (kpis.vencidos / kpis.certificados) * 100 : 0;

    return (
        <Box className='mainBox nrs-bi-page'>
            <Alerta
                texto={message}
                status={status}
                open={openAlerta}
                handleClose={() => setOpenAlerta(false)}
            />
            <PageHeader>Controle de NRs</PageHeader>

            <Box className='nrs-bi-shell'>
                <Box className='nrs-bi-hero'>
                    <Box>
                        <h2>Controle de NRs Setta Serviços</h2>
                        <p>Validade dos treinamentos NR-10, NR-35 e NR-10 SEP por funcionário e site.</p>
                    </Box>

                    <Box className='nrs-bi-actions'>
                        <FormControl className='nrs-bi-site-filter'>
                            <InputLabel id='labelSiteNRsBI'>Site</InputLabel>
                            <Select
                                labelId='labelSiteNRsBI'
                                label='Site'
                                value={selectedSite}
                                onChange={(event) => setSelectedSite(event.target.value)}
                            >
                                <MenuItem value='all'>Todos</MenuItem>
                                {sites.map((site) => (
                                    <MenuItem key={site} value={site}>
                                        {site}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Button
                            variant='contained'
                            startIcon={<GppGoodRoundedIcon />}
                            endIcon={<ArrowForwardRoundedIcon />}
                            onClick={() => navigate('/OeM/atualizarNRs')}
                        >
                            Atualizar NRs
                        </Button>
                    </Box>
                </Box>

                <Box className='nrs-bi-kpi-grid'>
                    <Box className='nrs-bi-kpi is-primary'>
                        <SchoolRoundedIcon />
                        <Box>
                            <span>Funcionários</span>
                            <strong>{kpis.funcionarios}</strong>
                        </Box>
                    </Box>

                    <Box className='nrs-bi-kpi'>
                        <WarningAmberRoundedIcon />
                        <Box>
                            <span>NRs a vencer</span>
                            <strong>{kpis.aVencer}</strong>
                        </Box>
                    </Box>

                    <Box className='nrs-bi-kpi'>
                        <ReportProblemRoundedIcon />
                        <Box>
                            <span>NRs vencidas</span>
                            <strong>{kpis.vencidos}</strong>
                        </Box>
                    </Box>

                    <Box className='nrs-bi-health'>
                        <Box className='nrs-bi-health-heading'>
                            <InsightsRoundedIcon />
                            <Box>
                                <span>Distribuição</span>
                                <strong>{selectedSiteLabel}</strong>
                            </Box>
                        </Box>
                        <Box className='nrs-bi-health-bar'>
                            <span className='is-ok' style={{ width: `${okPercent}%` }} />
                            <span className='is-warning' style={{ width: `${warningPercent}%` }} />
                            <span className='is-expired' style={{ width: `${expiredPercent}%` }} />
                        </Box>
                        <Box className='nrs-bi-health-legend'>
                            <span><i className='is-ok' /> Vigentes: {kpis.vigentes}</span>
                            <span><i className='is-warning' /> A vencer: {kpis.aVencer}</span>
                            <span><i className='is-expired' /> Vencidas: {kpis.vencidos}</span>
                        </Box>
                    </Box>
                </Box>

                <Box className='nrs-bi-table-panel'>
                    <Box className='nrs-bi-table-toolbar'>
                        <Box>
                            <span><FilterAltRoundedIcon /> Filtro ativo</span>
                            <strong>{selectedSiteLabel}</strong>
                        </Box>
                        <Box>
                            <span><FactCheckRoundedIcon /> Sites</span>
                            <strong>{visibleSiteCount}</strong>
                        </Box>
                    </Box>

                    <Box className='nrs-bi-table-scroll'>
                        <table className='nrs-bi-table'>
                            <thead>
                                <tr>
                                    <th>Funcionário</th>
                                    <th>Função</th>
                                    <th>Site</th>
                                    {nrColumns.map((column) => (
                                        <React.Fragment key={column.label}>
                                            <th>{column.label}</th>
                                            <th>Validade</th>
                                        </React.Fragment>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRegistros.map((registro) => (
                                    <tr key={`${registro.funcionario}-${registro.site}`}>
                                        <td>{registro.funcionario}</td>
                                        <td>{registro.funcao}</td>
                                        <td>{registro.site}</td>
                                        {nrColumns.map((column) => {
                                            const itemStatus = getStatus(registro[column.validityKey]);

                                            return (
                                                <React.Fragment key={column.label}>
                                                    <td>
                                                        {itemStatus && (
                                                            <span className={`nrs-bi-status ${itemStatus.className}`}>
                                                                {itemStatus.icon}
                                                                {itemStatus.label}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td>{formatDatePtBr(registro[column.validityKey])}</td>
                                                </React.Fragment>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default ControleNRsBI;
